import React, { useEffect, useState, useCallback } from 'react';
import { css } from '@emotion/css';
import * as yup from 'yup';
import { FormikProps, withFormik } from 'formik';
import TemplateFormSectionModel from '../../../../shared/models/templating/api/TemplateFormSectionModel';
import ApplicationContainer from '../../../../shared/state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import TemplateFormFieldValueModel from '../../../../shared/models/templating/api/TemplateFormFieldValueModel';
import AppliedApplicationTemplateModel from '../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import TemplateFormFieldModel from '../../../../shared/models/templating/api/TemplateFormFieldModel';
import Loader from '../../../../shared/components/general/Loader';
import PanelHeaderContainer from '../../../../shared/components/structure/Panels/PanelHeaderContainer';
import Button from '../../../../shared/components/general/Button';
import AppliedApplicationTemplateSectionForm from './AppliedApplicationTemplateSectionForm';
import styled from '@emotion/styled';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';
import TemplateSaveChangesModal from '../../../../shared/components/templating/TemplateSaveChangesModal';
import { sectionHasVisibleFields } from '../../../../shared/utils/sectionUtils';
import { resolveConditionalDefaultsForSubmit } from '../../../../shared/utils/conditionalDefaultUtils';
import { dedupeFieldValuesForField, findFieldValueForField, normalizeFieldValueForField } from '../../../../shared/utils/templateFieldMatching';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from '../../../../shared/constants/featureFlagIds';
import * as voicifyApi from '../../../../shared/api';
import { TelephonyDeploymentResponse } from '../../../../shared/models/telephonyDeployment/TelephonyDeploymentResponse';
import { color_colors_gold, color_variants_gold_light, color_shades_darkest } from '../../../../shared/constants/colors';

const instructionsIcon = require('../../../../shared/content/images/info_icon.svg');
const warningIcon = require('../../../../shared/content/images/alert-triangle-yellow.svg');

interface AppliedAppTemplatesManagerProps {
    applicationId: string
    organizationId?: string
    templateSection: TemplateFormSectionModel
    appContainer: ApplicationContainer
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    appliedAppValues: TemplateFormFieldValueModel[]
    appliedAppTemplate: AppliedApplicationTemplateModel
    onNext: (latestFormValues: TemplateFormFieldValueModel[]) => void
    onPrevious: () => void
    insertDynamicFormSection: (section: TemplateFormSectionModel) => void
    history: any
    currentSectionIndex?: number
    importedFormFields: TemplateFormFieldValueModel[]
    setImportedFormFields: (fields: TemplateFormFieldValueModel[]) => void
    calculateIncompleteSections: (templateFormFieldValues?: TemplateFormFieldValueModel[]) => void
    showAdminFields?: boolean
    onFieldValidationChange?: (fieldId: string, hasErrors: boolean) => void
}

interface AppliedAppTemplatesManagerFormData {
    templateFormValues: TemplateFormFieldValueModel[]
}

const InnerForm: React.FC<AppliedAppTemplatesManagerProps & FormikProps<AppliedAppTemplatesManagerFormData>> = (props) => {
    const [recentlyAddedModuleFormFields, setRecentlyAddedModuleFormFields] = useState<TemplateFormFieldModel[]>();
    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
    const [fieldValidationErrors, setFieldValidationErrors] = useState<Record<string, boolean>>({});
    const [telephonyDeployments, setTelephonyDeployments] = useState<TelephonyDeploymentResponse[]>([]);
    const [sharedTemplates, setSharedTemplates] = useState<any[]>([]);
    const lastSyncedValuesRef = React.useRef<typeof props.appliedAppValues>([]);

    useEffect(() => {
        const loadTelephony = async () => {
            if (!props.applicationId) return;
            try {
                const result = await voicifyApi.getTelephonyDeployments(props.applicationId);
                if (result.resultType === 'Ok') {
                    setTelephonyDeployments(result.data);
                }
            } catch (e) {
                console.error('Failed to load telephony deployments', e);
            }
        };
        loadTelephony();
    }, [props.applicationId]);

    useEffect(() => {
        const loadSharedTemplates = async () => {
            const currentApp = props.appContainer.state.currentApp;
            if (!currentApp) return;
            const orgId = (currentApp as any).organizationId;
            if (!orgId) return;

            try {
                const appsResult = await voicifyApi.getApplicationsWithFeaturesForOrg(orgId);
                if (appsResult.resultType === 'Ok' && appsResult.data) {
                    const sharedConfigApp = appsResult.data.find(app => 
                        app.name === "Shared Configuration" || 
                        app.allowsTemplating || 
                        app.name === "Shared Topics"
                    );
                    if (sharedConfigApp && sharedConfigApp.id) {
                        const templatesResult = await voicifyApi.getAppliedApplicationTemplatesForApp(sharedConfigApp.id);
                        if (templatesResult.resultType === 'Ok') {
                            setSharedTemplates(templatesResult.data);
                        }
                    }
                }
            } catch (e) {
                console.error('Failed to load shared templates', e);
            }
        };
        loadSharedTemplates();
    }, [props.appContainer.state.currentApp]);

    const requiredFields = props.templateSection?.templateFormFields?.filter(f => f.isRequired) ?? [];
    const combinedRequiredFields = [...requiredFields, ...(props.templateSection?.dynamicFormFields?.filter(f => f.isRequired) ?? [])];
    const completeFields = combinedRequiredFields.filter(f => {
        const matchingValue = props.values.templateFormValues?.find(v =>
            (v.templateFormFieldId == f.id)
        );

        if (!matchingValue) {
            return false;
        }

        const hasValidValue = matchingValue.value !== null &&
            matchingValue.value !== undefined &&
            matchingValue.value !== '' &&
            matchingValue.value.trim() !== '';

        return hasValidValue;
    });
    const hasValidationErrors = Object.values(fieldValidationErrors).some(hasError => hasError);

    const currentApp = props.appContainer.state.currentApp;
    const isAiOrdering = currentApp?.featureFlags?.some(ff => ff.id === FoodOrderingFeatureFlagId) ?? false;
    const warningMessages = (() => {
        const msgs: string[] = [];
        if (!(currentApp?.isLive ?? false)) return msgs;

        const getFieldValueFromForm = (variable: string): string => {
            const fieldValues = props.values.templateFormValues ?? [];
            const sections = props.appliedAppTemplate?.templateForm?.templateFormSections ?? [];
            const field = sections
                .flatMap(s => s.templateFormFields ?? [])
                .find(f => f.associatedVariable === variable);
            const fieldValueObj = field
                ? fieldValues.find(fv => fv.templateFormFieldId === field.id || fv.associatedVariable === variable)
                : fieldValues.find(fv => fv.associatedVariable === variable);
            const val = (fieldValueObj?.value ?? field?.defaultValue ?? '').toString().trim();
            if (val !== '' && val !== variable) {
                return val;
            }
            return '';
        };

        const getFieldValueFromTemplates = (templates: any[], variable: string): string => {
            for (const appliedTemplate of templates) {
                const sections = appliedTemplate.templateForm?.templateFormSections ?? [];
                const field = sections
                    .flatMap(s => s.templateFormFields ?? [])
                    .find(f => f.associatedVariable === variable);
                const fieldValueObj = (appliedTemplate.templateFormFieldValues ?? []).find(
                    fv => fv.templateFormFieldId === field?.id || fv.associatedVariable === variable
                );
                const val = (fieldValueObj?.value ?? field?.defaultValue ?? '').toString().trim();
                if (val !== '' && val !== variable) {
                    return val;
                }
            }
            return '';
        };

        const getEffectiveFieldValueByVariable = (variable: string) => {
            const formVal = getFieldValueFromForm(variable);
            if (formVal) return formVal;
            return getFieldValueFromTemplates(sharedTemplates, variable);
        };

        const sandboxIssues: string[] = [];
        const otherIssues: string[] = [];

        // 1. AI Ordering checks
        if (isAiOrdering) {
            const sections = props.appliedAppTemplate?.templateForm?.templateFormSections ?? [];
            const testOrderField = sections
                .flatMap(s => s.templateFormFields ?? [])
                .find(f => f.associatedVariable === '{Webhook-Is-Test-Order}');
            const fieldValues = props.values.templateFormValues ?? [];
            const fieldValue = testOrderField
                ? fieldValues.find(fv => fv.templateFormFieldId === testOrderField.id || fv.associatedVariable === '{Webhook-Is-Test-Order}')
                : fieldValues.find(fv => fv.associatedVariable === '{Webhook-Is-Test-Order}');
            const val = (fieldValue?.value ?? '').toString().toLowerCase();
            if (val === 'true') {
                otherIssues.push('this location is marked as a test app, but it is currently live (please set the Is Test field to False)');
            }

            const restaurantId = getEffectiveFieldValueByVariable('{Webhook-Restaurant-Id}').trim();
            if (restaurantId === '10138') {
                sandboxIssues.push('Admin - Location ID (10138)');
            } else if (restaurantId === '6716858da59fcf87d60ad76b') {
                sandboxIssues.push('Admin - Location ID (6716858da59fcf87d60ad76b)');
            } else if (restaurantId.toLowerCase() === 'af8e3bf4-a16b-4cca-b7a7-bb1be0a16021') {
                sandboxIssues.push('Admin - Location ID (af8e3bf4-a16b-4cca-b7a7-bb1be0a16021)');
            }
        }

        // 2. Dental Healthcare check
        const isDental = currentApp?.featureFlags?.some(ff => ff.id === HealthcareSchedulingFeatureFlagId) ?? false;
        if (isDental) {
            const cancelCode = getEffectiveFieldValueByVariable('{DenticonCancelCode}').trim();
            if (cancelCode === '473') {
                sandboxIssues.push('Admin Cancellation Code ID (473)');
            }

            const referralCode = getEffectiveFieldValueByVariable('{denticon-referral-type-code}').trim().toLowerCase();
            if (referralCode === 'roo6' || referralCode === 'r006') {
                sandboxIssues.push('Admin Referral Type Code (ROO6)');
            }

            const defaultOfficeId = getEffectiveFieldValueByVariable('{defaultOfficeId}').trim();
            if (defaultOfficeId === '266') {
                sandboxIssues.push('Default Office ID (266)');
            } else if (defaultOfficeId === '25000000000392') {
                sandboxIssues.push('Default Office ID (25000000000392)');
            }

            const procedureTypes = getEffectiveFieldValueByVariable('{procedureTypes}');
            const normalizedProcedureTypes = procedureTypes.replace(/\s/g, '');
            const defaultDenticonJSON = `[ { "additionalProcedureTypeIdsToLookUp": null, "appointmentDuration": 30, "procedureTypeDescription": "Anything related to a routine visit, regular cleaning, check-up, dental visit, or cleaning.", "procedureTypeId": "39" } ]`.replace(/\s/g, '');
            const defaultDentrixJSON = `[   {     "additionalProcedureTypeIdsToLookUp": null,     "appointmentDuration": 30,     "procedureTypeDescription": "Anything related to a routine visit, regular cleaning, check-up,dental visit, or cleaning.",     "procedureTypeId": "25000000391638"   } ]`.replace(/\s/g, '');

            if (normalizedProcedureTypes === defaultDenticonJSON || normalizedProcedureTypes === defaultDentrixJSON) {
                sandboxIssues.push('Procedure Types');
            }

            const medId = getEffectiveFieldValueByVariable('{MedicalIntegrationConfigId}').trim().toUpperCase();
            if (medId === '22FA374D-7BA2-439F-A1AD-F4712DD882EF') {
                sandboxIssues.push('Medical Integration Config ID (22FA374D-7BA2-439F-A1AD-F4712DD882EF)');
            } else if (medId === '87AC3583-8BFC-439D-9F0B-C7A450CF5E2A') {
                sandboxIssues.push('Medical Integration Config ID (87AC3583-8BFC-439D-9F0B-C7A450CF5E2A)');
            }
        }

        // 3. Telephony proactive response prompt checks (for both Ordering and Dental)
        if (isAiOrdering || isDental) {
            const inboundDeployments = telephonyDeployments.filter(d => !d.useForOutboundCalling);
            if (inboundDeployments.length > 0) {
                inboundDeployments.forEach(d => {
                    const isSpanish = d.locale?.toLowerCase().startsWith('es') ?? false;
                    let expectedConstant = '';
                    if (isAiOrdering) {
                        expectedConstant = isSpanish 
                            ? '{{constant.generative_proactive_response_ordering_spanish}}' 
                            : '{{constant.proactive_response_prompt}}';
                    } else if (isDental) {
                        expectedConstant = isSpanish 
                            ? '{{constant.generative_proactive_response_dental_spanish}}' 
                            : '{{constant.generative_proactive_response_dental}}';
                    }

                    const hasProactivePrompt = d.useGenerativeProactiveResponse && (d.generativeProactiveResponsePrompt ?? '').trim().length > 0;
                    const nameText = d.name?.trim() ? `telephony deployment '${d.name.trim()}'` : 'an inbound telephony deployment';

                    if (!hasProactivePrompt) {
                        otherIssues.push(`proactive response prompt is missing on ${nameText}`);
                    } else if (d.generativeProactiveResponsePrompt?.trim() !== expectedConstant) {
                        otherIssues.push(`proactive response prompt on ${nameText} should use constant '${expectedConstant}'`);
                    }
                });
            } else {
                otherIssues.push('no proactive response is configured in telephony settings');
            }
        }

        const totalIssuesCount = sandboxIssues.length + otherIssues.length;
        if (totalIssuesCount === 1) {
            if (sandboxIssues.length === 1) {
                const item = sandboxIssues[0];
                if (item.includes('Admin - Location ID (10138)')) {
                    msgs.push('Admin - Location ID is using the sandbox value (10138). Please update with client POS details.');
                } else if (item.includes('Admin - Location ID (6716858da59fcf87d60ad76b)')) {
                    msgs.push('Admin - Location ID is using the sandbox value (6716858da59fcf87d60ad76b). Please update with client POS details.');
                } else if (item.includes('Admin - Location ID (af8e3bf4-a16b-4cca-b7a7-bb1be0a16021)')) {
                    msgs.push('Admin - Location ID is using the sandbox value (af8e3bf4-a16b-4cca-b7a7-bb1be0a16021). Please update with client POS details.');
                } else if (item.includes('Cancellation Code')) {
                    msgs.push('Admin Cancellation Code ID is using the sandbox value (473). Please update with client PMS details.');
                } else if (item.includes('Referral Type')) {
                    msgs.push('Admin Referral Type Code is using the sandbox value (ROO6). Please update with client PMS details.');
                } else if (item.includes('Default Office ID (266)')) {
                    msgs.push('Default Office ID is using the sandbox value (266). Please update with client PMS details.');
                } else if (item.includes('Default Office ID (25000000000392)')) {
                    msgs.push('Default Office ID is using the sandbox value (25000000000392). Please update with client PMS details.');
                } else if (item.includes('Procedure Types')) {
                    msgs.push('Procedure Types is using sandbox default values. Please update with client PMS details.');
                } else if (item.includes('22FA374D-7BA2-439F-A1AD-F4712DD882EF')) {
                    msgs.push('Medical Integration Config ID is using the sandbox value (22FA374D-7BA2-439F-A1AD-F4712DD882EF). Please update with client PMS details.');
                } else if (item.includes('87AC3583-8BFC-439D-9F0B-C7A450CF5E2A')) {
                    msgs.push('Medical Integration Config ID is using the sandbox value (87AC3583-8BFC-439D-9F0B-C7A450CF5E2A). Please update with client PMS details.');
                }
            } else if (otherIssues.length === 1) {
                const issue = otherIssues[0];
                if (issue.includes('test app')) {
                    msgs.push('This location is marked as a test app, but it is currently live. Please set the Is Test field to False.');
                } else if (issue.includes('proactive response')) {
                    msgs.push(`${issue.charAt(0).toUpperCase() + issue.slice(1)}.`);
                } else {
                    msgs.push('No proactive response is configured in telephony settings.');
                }
            }
        } else if (totalIssuesCount > 1) {
            let sentence = '';
            if (sandboxIssues.length > 0) {
                const joinedSandbox = sandboxIssues.join(', ').replace(/, ([^,]*)$/, " and $1");
                sentence += `the following fields are using sandbox values: ${joinedSandbox}`;
            }

            if (otherIssues.length > 0) {
                const joinedOther = otherIssues.join(' and ');
                if (sentence) {
                    sentence += `, and ${joinedOther}`;
                } else {
                    sentence += `${joinedOther}`;
                }
            }

            sentence = `${sentence.charAt(0).toUpperCase() + sentence.slice(1)}.`;
            msgs.push(sentence);
        }

        return msgs;
    })();

    const handleFieldValidationChange = useCallback((fieldId: string, hasErrors: boolean) => {
        setFieldValidationErrors(prev => ({
            ...prev,
            [fieldId]: hasErrors
        }));
        // Notify parent component of validation state change
        if (props.onFieldValidationChange) {
            props.onFieldValidationChange(fieldId, hasErrors);
        }
    }, [props.onFieldValidationChange]);
    // Check if section has visible fields on mount and when values change
    useEffect(() => {
        if (!props.templateSection) return;

        const hasVisibleFields = sectionHasVisibleFields(
            props.templateSection,
            props.showAdminFields ?? true,
            props.values.templateFormValues
        );

        // If no visible fields, automatically navigate to next section
        if (!hasVisibleFields) {
            // Small delay to ensure state is settled
            setTimeout(() => {
                props.onNext(props.values.templateFormValues);
            }, 0);
        }
    }, [props.templateSection, props.values.templateFormValues, props.showAdminFields]);

    useEffect(() => {
        const allSections = props.appliedAppTemplate?.templateForm?.templateFormSections ?? [];
        const allFields = allSections.flatMap(s => s.templateFormFields);
        if (props.importedFormFields.length > 0) {
            for (const templateFormField of props.importedFormFields) {
                const matchingFormField = allFields?.find(formField => formField.title === templateFormField.title);
                if (matchingFormField) {
                    onDynamicFormValueChanged(matchingFormField, templateFormField.value);
                };
            };
        };
        props.calculateIncompleteSections(props.importedFormFields && props.importedFormFields);
    }, [props.importedFormFields]);

    useEffect(() => {
        const recentlyAddedFields = props.templateSection?.templateFormFields?.filter(f => f.recentlyAdded) ?? [];
        for (const templateFormSection of props.appliedAppTemplate?.templateForm?.templateFormSections ?? []) {
            for (const templateFormField of templateFormSection?.templateFormFields) {
                if (!props.appliedAppTemplate?.templateFormFieldValues.find(templateFormFieldValue => templateFormFieldValue.title === templateFormField.title)) {
                    recentlyAddedFields.push({ ...templateFormField, recentlyAdded: true });
                }
            }
        }
        setRecentlyAddedModuleFormFields(recentlyAddedFields);
    }, []);

    useEffect(() => {
        if (props.appliedAppValues == null) return;
        // Only re-sync into Formik state when the incoming prop values have actually changed
        // (e.g. after autogenerate updates the container). This prevents clobbering in-progress
        // user edits on normal re-renders while still reflecting external updates like autogenerate.
        const currentJson = JSON.stringify(lastSyncedValuesRef.current);
        const nextJson = JSON.stringify(props.appliedAppValues);
        if (currentJson === nextJson) return;
        lastSyncedValuesRef.current = JSON.parse(nextJson);
        const updatedFieldValues = [...props.appliedAppValues];
        for (const templateFormSection of props.appliedAppTemplate?.templateForm?.templateFormSections ?? []) {
            for (const templateFormField of templateFormSection?.templateFormFields) {
                if (!props.appliedAppTemplate?.templateFormFieldValues.find(templateFormFieldValue => templateFormFieldValue.title === templateFormField.title)) {
                    updatedFieldValues.push({
                        fieldType: templateFormField.fieldType,
                        tip: templateFormField.tip,
                        title: templateFormField.title,
                        label: templateFormField.label,
                        defaultValue: templateFormField.defaultValue,
                        associatedVariable: templateFormField.associatedVariable,
                        placeholder: templateFormField.placeholder,
                        templateFormFieldId: templateFormField.id
                    });
                }
            }
        }
        props.setFieldValue("templateFormValues", updatedFieldValues);
    }, [props.appliedAppValues]);

    const onDynamicFormValueChanged = (field: TemplateFormFieldModel, value: string) => {
        const templateValues = field.name
            ? [...(props.values.templateFormValues ?? [])]
            : dedupeFieldValuesForField(field, props.values.templateFormValues ?? []);
        // if we have the value, update it
        const existingField = field.name
            ? templateValues.find(v => v.dynamicFormFieldName == field.name)
            : findFieldValueForField(field, templateValues);
        if (existingField != null) {
            const index = templateValues.indexOf(existingField);
            templateValues[index] = field.name
                ? { ...existingField, value }
                : normalizeFieldValueForField(field, { ...existingField, value });
        }
        // if we don't, add it
        else {
            const fieldValue: TemplateFormFieldValueModel = {
                value: value,
                fieldType: field.fieldType,
                tip: field.tip,
                title: field.title,
                label: field.label,
                defaultValue: field.defaultValue,
                associatedVariable: field.associatedVariable,
                placeholder: field.placeholder
            };
            if (field.name)
                fieldValue.dynamicFormFieldName = field.name;
            else
                fieldValue.templateFormFieldId = field.id;
            templateValues.push(fieldValue);
        }
        props.setFieldValue("templateFormValues", templateValues);
        props.calculateIncompleteSections(templateValues);
    }

    /**
     * Atomic multi-field updater — applies all updates to a single snapshot of
     * templateFormValues and calls setFieldValue once, preventing the stale-closure
     * race condition that occurs when onDynamicFormValueChanged is called twice in
     * the same synchronous event (e.g. overnight split-save).
     */
    const onMultipleDynamicFormValuesChanged = (updates: Array<{ field: TemplateFormFieldModel, value: string }>) => {
        let templateValues = [...(props.values.templateFormValues ?? [])];
        for (const { field, value } of updates) {
            if (!field.name) {
                templateValues = dedupeFieldValuesForField(field, templateValues);
            }
            const existingField = field.name
                ? templateValues.find(v => v.dynamicFormFieldName == field.name)
                : findFieldValueForField(field, templateValues);
            if (existingField != null) {
                const index = templateValues.indexOf(existingField);
                templateValues[index] = field.name
                    ? { ...existingField, value }
                    : normalizeFieldValueForField(field, { ...existingField, value });
            } else {
                const fieldValue: TemplateFormFieldValueModel = {
                    value,
                    fieldType: field.fieldType,
                    tip: field.tip,
                    title: field.title,
                    label: field.label,
                    defaultValue: field.defaultValue,
                    associatedVariable: field.associatedVariable,
                    placeholder: field.placeholder
                };
                if (field.name)
                    fieldValue.dynamicFormFieldName = field.name;
                else
                    fieldValue.templateFormFieldId = field.id;
                templateValues.push(fieldValue);
            }
        }
        props.setFieldValue("templateFormValues", templateValues);
        props.calculateIncompleteSections(templateValues);
    }

    const isLoading = props.appliedAppTemplateContainer.state.isLoading || props.appliedAppTemplateContainer.state.isSaving;

    const handleSaveForm = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        setShowSaveChangesModal(true);
    };

    const performSave = async () => {
        const allFields = props.appliedAppTemplate?.templateForm?.templateFormSections?.flatMap(s => s.templateFormFields ?? []) ?? [];
        const resolvedValues = resolveConditionalDefaultsForSubmit(allFields, props.values.templateFormValues);
        await props.appliedAppTemplateContainer.saveTemplateFields(
            props.applicationId,
            props.appliedAppTemplate.id,
            resolvedValues,
            {
                finalize: false,
                useModifiedFieldsOnly: true
            }
        );
    };

    const closeSaveChangesModal = () => {
        setShowSaveChangesModal(false);
    };

    const handleNextSection = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        // Resolve conditional defaults for fields the user never touched before handing
        // values up to the parent for navigation / finalization.
        const allFields = props.appliedAppTemplate?.templateForm?.templateFormSections?.flatMap(s => s.templateFormFields ?? []) ?? [];
        const resolvedValues = resolveConditionalDefaultsForSubmit(allFields, props.values.templateFormValues);
        // Pass latest form values to parent for navigation
        props.onNext(resolvedValues);
    };

    // Check if the current section has visible fields
    const hasVisibleFields = props.templateSection
        ? sectionHasVisibleFields(
            props.templateSection,
            props.showAdminFields ?? true,
            props.values.templateFormValues
        )
        : false;

    // Don't render the form if there are no visible fields
    // The useEffect above will handle navigation
    if (!hasVisibleFields) {
        return <Loader />;
    }

    return (
        <>
            {
                props.appliedAppTemplateContainer.state.isLoading ?
                    <Loader /> :
                    <form onSubmit={handleNextSection}>
                        <PanelHeaderContainer className={warningMessages.length > 0 ? warningHeaderStyle : ''}>
                            {warningMessages.length > 0 ? (
                                <WarningContent style={{ padding: '12px 16px' }}>
                                    <InstructionsIcon src={warningIcon} style={{ margin: '0 8px 0 0', alignSelf: 'flex-start', marginTop: '2px' }} />
                                    <InstructionsText className={warningItemStyle}>
                                        {warningMessages[0].startsWith('Warning:') ? warningMessages[0] : `Warning: ${warningMessages[0]}`}
                                    </InstructionsText>
                                </WarningContent>
                            ) : (
                                <>
                                    <InstructionsIcon src={instructionsIcon} />
                                    <InstructionsText>Complete the fields below</InstructionsText>
                                </>
                            )}
                            <div className={buttonContainerStyle}>
                                {/* These are the buttons in the top right of the Spark form */}
                                {(props.currentSectionIndex != null && props.currentSectionIndex > 0) &&
                                    <PreviousButton
                                        disabled={props.appliedAppTemplateContainer.state.isLoading}
                                        themes={["white-small"]}
                                        text="Previous"
                                        type="button"
                                        onClick={props.onPrevious}
                                    />
                                }
                                <SaveButton
                                    loading={isLoading}
                                    disabled={hasValidationErrors}
                                    themes={["secondary-small"]}
                                    text="Save"
                                    type="button"
                                    onClick={handleSaveForm}
                                />
                                <NextButton
                                    disabled={hasValidationErrors || (!isLoading && (props.appliedAppTemplate?.templateForm?.templateConfiguration?.dynamicFormUrl != null) && (combinedRequiredFields.length != completeFields.length))}
                                    loading={isLoading}
                                    themes={["primary-small"]}
                                    text="Next"
                                    type="button"
                                    onClick={handleNextSection}
                                />
                            </div>
                        </PanelHeaderContainer>
                        {props.templateSection &&
                            <AppliedApplicationTemplateSectionForm
                                onPrevious={props.onPrevious}
                                submitForm={props.submitForm}
                                combinedRequiredFields={combinedRequiredFields}
                                completeFields={completeFields}
                                appliedAppTemplateContainer={props.appliedAppTemplateContainer}
                                appliedAppTemplate={props.appliedAppTemplate}
                                key={props.templateSection?.name ?? props.templateSection?.id}
                                section={props.templateSection}
                                values={props.values.templateFormValues}
                                templateFormValues={props.values.templateFormValues}
                                applicationId={props.applicationId}
                                organizationId={props.organizationId}
                                onFieldValueChange={onDynamicFormValueChanged}
                                onMultipleFieldValuesChange={onMultipleDynamicFormValuesChanged}
                                recentlyAddedModuleFormFields={recentlyAddedModuleFormFields}
                                setFieldValue={props.setFieldValue}
                                showAdminFields={props.showAdminFields}
                                appContainer={props.appContainer}
                                onFieldValidationChange={handleFieldValidationChange}
                                allFormFields={props.appliedAppTemplate?.templateForm?.templateFormSections?.flatMap(s => s.templateFormFields ?? []) ?? []}
                                hasValidationErrors={hasValidationErrors}
                            />
                        }
                    </form>
            }
            <TemplateSaveChangesModal
                showSaveChangesModal={showSaveChangesModal}
                closeModal={closeSaveChangesModal}
                changedFields={props.appliedAppTemplateContainer.getChangedFieldsForDisplay(
                    props.appliedAppTemplate.id,
                    props.values.templateFormValues
                )}
                submitChanges={performSave}
                isLoading={props.appliedAppTemplateContainer.state.isSaving}
                hasErrors={hasValidationErrors}
            />
        </>
    )
}

const PreviousButton = styled(Button)``;

const SaveButton = styled(Button)`
    margin-right: 0;
`;

const NextButton = styled(Button)`
    margin-left: 0;
`;

const InstructionsIcon = styled.img`
    width: 24px;
    height: 24px;
    margin: 0 8px 0 32px;
    ${breakpoint_small} {
        display: none;
    }
`;

const InstructionsText = styled.p`
    ${breakpoint_small} {
        display: none;
    }
`;

const WarningContent = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
`;

const testOrderWarningTextStyle = css`
    color: ${color_shades_darkest};
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${breakpoint_small} {
        display: none;
    }
`;

const warningItemStyle = css`
    color: ${color_shades_darkest};
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    ${breakpoint_small} {
        display: none;
    }
`;

const warningHeaderStyle = css`
    background-color: ${color_variants_gold_light};
    border-bottom: 1px solid ${color_colors_gold};
`;

const buttonContainerStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 16px;
    margin-left: auto;
    button {
        margin: 0 16px 0 0;
    }
    ${breakpoint_small} {
        margin-right: 0;
    }
`;

const UpdateAppliedAppTemplateForm = withFormik<AppliedAppTemplatesManagerProps, AppliedAppTemplatesManagerFormData>({
    mapPropsToValues: props => {
        return {
            templateFormValues: [...(props.appliedAppValues ?? [])]
        }
    },
    validationSchema: yup.object().shape({
    }),
    handleSubmit: async (values, { props, setFieldError }) => {
        // Resolve conditional defaults before passing values up to parent
        const allFields = props.appliedAppTemplate?.templateForm?.templateFormSections?.flatMap(s => s.templateFormFields ?? []) ?? [];
        const resolvedValues = resolveConditionalDefaultsForSubmit(allFields, values.templateFormValues);
        props.onNext(resolvedValues);
    }
})(InnerForm);


export default UpdateAppliedAppTemplateForm;
